import { DatePipe, NgTemplateOutlet } from '@angular/common';
import {
	Component,
	ElementRef,
	Input,
	OnChanges,
	OnInit,
	SimpleChanges,
	ViewChild,
} from '@angular/core';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { getEnvironment } from 'src/lib/environment/environment';
import { IfEmptyPipe } from 'src/lib/pipes/if-empty.pipe';
import { UserUpdateRequestModel } from 'src/lib/services/api/users/update-request/users-update-request.model';
import {
	UserDataProfileModel,
	UserDataStudentModel,
} from 'src/lib/services/api/users/user-data.model';
import {
	hasKey,
	hasSomeKey,
	isNullOrEmptyString,
} from 'src/lib/utilities/compare';
import { IconTooltipComponent } from '../../../../../templates/global/icon-tooltip/icon-tooltip.component';
import { SpinWhileDirective } from '../../../../../templates/layout/spin-while/spin-while.directive';
import { NgDompurifyPipe } from '../../../../../thirdparty/ng-dompurify/ng-dompurify.pipe';

//First, Middle, Last, Suffix, Maiden, Preferred, Birthdate, Race, Gender, Phone, Email, Address
interface UpdatedValue {
	old: unknown;
	new: unknown;
	proof: boolean;
}

@Component({
	selector: 'ae-profile-info-view',
	templateUrl: './profile-info-view.component.html',
	styleUrls: ['./profile-info-view.component.scss'],
	imports: [
		DatePipe,
		IconTooltipComponent,
		IfEmptyPipe,
		NgbTooltip,
		NgDompurifyPipe,
		NgTemplateOutlet,
		SpinWhileDirective,
	],
})
export class ProfileInfoViewComponent implements OnInit, OnChanges {
	@Input() public profileData: UserDataProfileModel;
	@Input() public studentData: UserDataStudentModel;
	@Input() public requests: UserUpdateRequestModel[] = [];

	@Input() public canEditProfile: boolean;
	@Input() public loading: boolean;
	@Input() public enableEdit: () => void;

	@ViewChild('rootElement', { static: true })
	rootElement: ElementRef<HTMLElement>;

	public isStudentApp = getEnvironment().isApp('student');

	public hasKey = hasKey;
	public hasSomeKey = hasSomeKey;

	protected updatedStudentInfoMap = new Map<string, UpdatedValue>();

	ngOnInit() {
		this.reload();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['requests']) {
			this.reload();
		}
	}

	private reload = () => {
		this.getPendingField();
		this.getNameUpdate();
	};

	public getMaidenName = () => {
		if (
			isNullOrEmptyString(this.profileData?.profile_maiden_name) &&
			!this.updatedStudentInfoMap.get('profile_maiden_name')?.new
		) {
			return '';
		} else {
			return `(${this.updatedStudentInfoMap.get('profile_maiden_name')?.new ?? this.profileData.profile_maiden_name})`;
		}
	};

	public welcomeVideo = (videoId: string): string | null => {
		if (videoId) {
			return `https://www.youtube.com/watch?v=${videoId}`;
		}
		return null;
	};

	protected getNameUpdate = () => {
		const types = [
			'profile_first_name',
			'profile_middle_name',
			'profile_last_name',
			'profile_suffix',
			'profile_maiden_name',
			'profile_preferred_name',
			'profile_pronunciation',
		];

		return (
			types.find((t) => {
				return this.updatedStudentInfoMap.get(t)?.new != null;
			}) != null
		);
	};

	protected getPendingField = () => {
		//First, Middle, Last, Suffix, Maiden, Preferred, Birthdate, Race, Gender, Phone, Email, Address
		const types = [
			'profile_first_name',
			'profile_middle_name',
			'profile_last_name',
			'profile_suffix',
			'profile_maiden_name',
			'profile_preferred_name',
			'profile_pronunciation',
			'profile_field_race',
			'profile_field_ethnicity',
			'profile_student_gender',
			'dob',
		];

		this.requests = this.requests?.sort((a, b) => {
			const aDate = a.requested.on.getTime();
			const bDate = b.requested.on.getTime();
			return aDate > bDate ? -1 : aDate < bDate ? 1 : 0;
		});

		types.forEach((type) => {
			const update: UserUpdateRequestModel = this.requests?.find((request) => {
				if (type === 'dob') {
					return request.updates?.student_data
						? request.updates?.student_data[type] != null
						: false;
				} else {
					return request.updates?.profile
						? request.updates?.profile[type] != null
						: false;
				}
			});

			const getOldValue = (val: string) => {
				if (isNullOrEmptyString(val)) {
					return null;
				}
				if (type === 'dob') {
					return this.studentData && this.studentData.dob
						? this.studentData[type]
						: null;
				} else {
					return this.profileData && this.profileData[type]
						? this.profileData[type]
						: null;
				}
			};

			this.updatedStudentInfoMap.set(type, {
				old: getOldValue(type),
				new:
					type === 'dob'
						? update?.updates?.student_data[type]?.new
						: update?.updates?.profile[type]?.new,
				proof: update ? update.updates?.[type]?.proofs?.length > 0 : false,
			});
		});
	};
}
